import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { CheckCircle2, XCircle, Zap, Clock, Calendar, User, Brain, ArrowLeftRight, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface Expert {
  id: string;
  name: string;
  nameEn: string;
  specialization: string;
  specializationEn: string;
  type: 'expert' | 'celebrity';
}

export interface LLMModel {
  id: string;
  name: string;
  provider: string;
  version: string;
}

interface AnalysisHeaderV3Props {
  title: string;
  result: "YES" | "NO" | "MAYBE";
  positiveCount: number; // Суммарная оценка плюсов
  negativeCount: number; // Суммарная оценка минусов
  positiveArgumentsCount: number; // Количество аргументов-плюсов
  negativeArgumentsCount: number; // Количество аргументов-минусов
  confidence: number;
  theme: 'dark' | 'light';
  hasCustomArguments?: boolean;
  customArgumentsCount?: number;
  language: 'ru' | 'en';
  expert?: Expert;
  llmModel?: LLMModel;
  question?: string; // Вопрос к запросу на анализ
  date?: Date | string; // Дата для отображения (дата анализа или дата шеринга)
  isOriginalModified?: boolean; // Флаг, что оригинальный анализ был изменён
  showOriginal?: boolean; // Показывать ли оригинальный анализ
  onToggleOriginal?: () => void; // Обработчик переключения
  isTransitioning?: boolean; // Флаг анимации переключения
  isLoadingOriginal?: boolean; // Флаг загрузки оригинального анализа
  transitionDelay?: number; // Задержка анимации
  plusListTitle?: string | null; // Заголовок для списка плюсов
  minusListTitle?: string | null; // Заголовок для списка минусов
}

function ModificationBanner({ 
  language, 
  theme,
  showOriginal,
  onToggle,
  hasToggle,
  isLoadingOriginal,
  isTransitioning
}: { 
  language: 'ru' | 'en'; 
  theme: 'dark' | 'light';
  showOriginal: boolean;
  onToggle: () => void;
  hasToggle?: boolean;
  isLoadingOriginal?: boolean;
  isTransitioning?: boolean;
}) {
  const { t } = useTranslation('analysis');
  const isDark = theme === 'dark';
  
  return (
    <Card className={`p-4 border-2 border-dashed ${
      isDark 
        ? 'bg-orange-500/10 border-orange-500/30' 
        : 'bg-orange-50 border-orange-200'
    }`}>
      <div className={`flex items-center ${hasToggle ? 'justify-between' : 'justify-start'} gap-3`}>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${
            isDark ? 'bg-orange-400' : 'bg-orange-500'
          }`} />
          <span className={`text-sm font-medium ${
            isDark ? 'text-orange-300' : 'text-orange-800'
          }`}>
            {t('analysisWasModified')}
          </span>
        </div>
        {hasToggle && (
          <button
            onClick={onToggle}
            disabled={isLoadingOriginal}
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors underline decoration-dotted underline-offset-2 ${
              isDark
                ? 'text-orange-300 hover:text-orange-200 disabled:text-orange-400/50 disabled:cursor-not-allowed'
                : 'text-orange-800 hover:text-orange-900 disabled:text-orange-600/50 disabled:cursor-not-allowed'
            }`}
          >
            {isLoadingOriginal ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowLeftRight 
                className={`w-4 h-4 ${showOriginal ? 'rotate-y-180' : ''}`}
                style={isTransitioning ? {
                  animation: showOriginal ? 'flipIconTo180 0.6s ease-in-out forwards' : 'flipIconTo0 0.6s ease-in-out forwards',
                  transformStyle: 'preserve-3d'
                } : {
                  transformStyle: 'preserve-3d'
                }}
              />
            )}
            {isLoadingOriginal ? t('loading') : showOriginal ? t('showModified') : t('showOriginal')}
          </button>
        )}
      </div>
    </Card>
  );
}

export function AnalysisHeaderV3({ 
  title, 
  result, 
  positiveCount, 
  negativeCount, 
  positiveArgumentsCount,
  negativeArgumentsCount,
  confidence,
  theme,
  hasCustomArguments = false,
  customArgumentsCount = 0,
  language,
  expert,
  llmModel,
  question,
  date,
  isOriginalModified = false,
  showOriginal = false,
  onToggleOriginal,
  isTransitioning = false,
  isLoadingOriginal = false,
  transitionDelay = 0,
  plusListTitle,
  minusListTitle
}: AnalysisHeaderV3Props) {
  const { t } = useTranslation('analysis');
  const isPositive = result === "YES";
  const isMaybe = result === "MAYBE";
  const total = positiveArgumentsCount + negativeArgumentsCount;
  const totalScore = positiveCount + negativeCount; // Общая сумма баллов для прогресс-баров
  const isDark = theme === 'dark';
  
  return (
    <div className="space-y-6">
      {/* Modification Banner */}
      {(isOriginalModified || hasCustomArguments) && (
        <ModificationBanner 
          language={language}
          theme={theme}
          showOriginal={showOriginal}
          onToggle={onToggleOriginal || (() => {})}
          hasToggle={!!isOriginalModified && !!onToggleOriginal}
          isLoadingOriginal={isLoadingOriginal}
          isTransitioning={isTransitioning}
        />
      )}

      <div className="relative">
        {/* Background gradient */}
        <div className={`absolute inset-0 rounded-3xl ${
          isDark 
            ? 'bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-slate-900/20'
            : 'bg-gradient-to-br from-purple-100/60 via-blue-100/60 to-gray-100/60'
        }`} />
        
        <Card className={`relative border-0 backdrop-blur-sm p-8 ${
          isDark 
            ? 'bg-slate-800/50' 
            : 'bg-white/80 shadow-xl'
        }`}>
        <div className="space-y-8">
          {/* Header info */}
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className={`flex items-center gap-2 text-sm flex-wrap ${
                isDark ? 'text-slate-400' : 'text-gray-600'
              }`}>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{(() => {
                    const dateObj = date 
                      ? (typeof date === 'string' ? new Date(date) : date)
                      : new Date();
                    return dateObj.toLocaleDateString('ru-RU');
                  })()}</span>
                  <span className="max-[640px]:hidden">•</span>
                </div>
                <div className="flex items-center gap-2 max-[640px]:w-full max-[640px]:mt-1">
                  <Clock className="w-4 h-4" />
                  <span>{t('analysisCompleted')}</span>
                </div>
              </div>
            </div>
            <Badge className={
              expert?.type === 'expert'
                ? (isDark 
                    ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                    : 'bg-purple-500/10 text-purple-700 border-purple-300')
                : expert?.type === 'celebrity'
                  ? (isDark 
                      ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                      : 'bg-yellow-500/10 text-yellow-700 border-yellow-300')
                  : (isDark 
                      ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                      : 'bg-blue-500/10 text-blue-700 border-blue-300')
            }>
              {expert ? (
                <>
                  <User className="w-3 h-3 mr-1" />
                  {language === 'ru' ? expert.name : expert.nameEn}
                </>
              ) : (
                <>
                  <Brain className="w-3 h-3 mr-1" />
                  {llmModel?.id === 'ai' ? t('aiName') : `${llmModel?.name ?? ''} ${llmModel?.version ?? ''}`.trim()}
                </>
              )}
            </Badge>
          </div>

          {/* Main result */}
          <div className="text-center space-y-6">
            <h1 className={`text-4xl heading-hero mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>{title}</h1>
            
            {/* Question to analysis request */}
            {question && (
              <div className="max-w-4xl mx-auto">
                <p className={`text-lg leading-relaxed ${
                  isDark ? 'text-slate-300' : 'text-gray-700'
                }`}>
                  <span className={`font-medium ${
                    isDark ? 'text-blue-300' : 'text-blue-600'
                  }`}>
                    {t('question')}
                  </span>
                  {question}
                </p>
              </div>
            )}
            
            {/* Result indicator */}
            <div className="flex items-center justify-center">
              <div className={`relative w-32 h-32 rounded-full flex items-center justify-center ${
                isPositive 
                  ? 'bg-gradient-to-br from-green-400/20 to-emerald-500/20 border-2 border-green-400/30' 
                  : isMaybe
                  ? 'bg-gradient-to-br from-blue-400/20 to-purple-500/20 border-2 border-blue-400/30'
                  : 'bg-gradient-to-br from-red-400/20 to-rose-500/20 border-2 border-red-400/30'
              }`}>
                {isPositive ? (
                  <CheckCircle2 className="w-16 h-16 text-green-400" />
                ) : isMaybe ? (
                  <Brain className="w-16 h-16 text-blue-400" />
                ) : (
                  <XCircle className="w-16 h-16 text-red-400" />
                )}
                
                {/* Animated ring */}
                <div className={`absolute inset-0 rounded-full animate-pulse ${
                  isPositive ? 'bg-green-400/10' : isMaybe ? 'bg-blue-400/10' : 'bg-red-400/10'
                } ${isTransitioning ? 'verdict-animate' : ''}`} />
              </div>
            </div>

            <div className="space-y-3">
              <div className={`text-6xl font-bold ${
                isPositive ? 'text-green-400' : isMaybe ? 'text-blue-400' : 'text-red-400'
              } ${isTransitioning ? 'verdict-animate' : ''}`}>
                {result === "YES" ? t('verdictYes') : result === "NO" ? t('verdictNo') : t('verdictMaybe')}
              </div>
              <p className={`text-xl max-w-2xl mx-auto ${
                isDark ? 'text-slate-300' : 'text-gray-700'
              }`}>
                {t('resultBasedOnFactors', { count: total })}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className={`bg-gradient-to-br from-green-500/10 to-emerald-600/10 border-green-500/20 p-6 ${
              !isDark && 'shadow-lg'
            } ${isTransitioning ? 'analysis-card-animate' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm uppercase tracking-wide ${
                    isDark ? 'text-green-300/80' : 'text-green-700/80'
                  }`}>{(plusListTitle && plusListTitle.trim()) || (language === 'ru' ? 'Плюсы' : 'Pros')}</p>
                  <p className={`text-3xl font-bold ${
                    isDark ? 'text-green-400' : 'text-green-600'
                  }`}>{positiveCount}</p>
                </div>
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className={`w-6 h-6 ${
                    isDark ? 'text-green-400' : 'text-green-600'
                  }`} />
                </div>
              </div>
              <div className="mt-4">
                <div className={`w-full h-2 rounded-full overflow-hidden ${
                  isDark ? 'bg-slate-700' : 'bg-gray-200'
                }`}>
                  <div 
                    className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${totalScore > 0 ? (positiveCount / totalScore) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </Card>

            <Card className={`bg-gradient-to-br from-red-500/10 to-rose-600/10 border-red-500/20 p-6 ${
              !isDark && 'shadow-lg'
            } ${isTransitioning ? 'analysis-card-animate' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm uppercase tracking-wide ${
                    isDark ? 'text-red-300/80' : 'text-red-700/80'
                  }`}>{(minusListTitle && minusListTitle.trim()) || t('cons')}</p>
                  <p className={`text-3xl font-bold ${
                    isDark ? 'text-red-400' : 'text-red-600'
                  }`}>{negativeCount}</p>
                </div>
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                  <XCircle className={`w-6 h-6 ${
                    isDark ? 'text-red-400' : 'text-red-600'
                  }`} />
                </div>
              </div>
              <div className="mt-4">
                <div className={`w-full h-2 rounded-full overflow-hidden ${
                  isDark ? 'bg-slate-700' : 'bg-gray-200'
                }`}>
                  <div 
                    className="h-full bg-gradient-to-r from-red-400 to-rose-500 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${totalScore > 0 ? (negativeCount / totalScore) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </Card>

            <Card className={`bg-gradient-to-br from-blue-500/10 to-purple-600/10 border-blue-500/20 p-6 ${
              !isDark && 'shadow-lg'
            } ${isTransitioning ? 'analysis-card-animate' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm uppercase tracking-wide ${
                    isDark ? 'text-blue-300/80' : 'text-blue-700/80'
                  }`}>{t('confidence')}</p>
                  <p className={`text-3xl font-bold ${
                    isDark ? 'text-blue-400' : 'text-blue-600'
                  }`}>{confidence}%</p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Zap className={`w-6 h-6 ${
                    isDark ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
              </div>
              <div className="mt-4">
                <div className={`w-full h-2 rounded-full overflow-hidden ${
                  isDark ? 'bg-slate-700' : 'bg-gray-200'
                }`}>
                  <div 
                    className="h-full bg-gradient-to-r from-blue-400 to-purple-500 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${confidence}%` }}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
}
